import { PURCHASE_LIMIT_BEHAVIOR_TYPES } from 'src/apis/dto/DTOProduct';
import type { DTOSubProductModule } from 'src/apis/dto/DTOShopModule';
import type { DTOGemProductSection, DTOSubProductsSection } from 'src/apis/dto/DTOShopSection';
import type { StarterPackageModule, SingleProductModule } from 'src/types/gemLack';
import type { GemProduct } from 'src/types/Payments';
import { getSaveRateByGemProductComparison } from 'src/utils/payments';

import type { SubscriptionProduct } from './SubscriptionCard';

export const toGemProducts = (section?: DTOGemProductSection): GemProduct[] =>
  (section?.modules ?? []).map((m) => ({
    productId: m.productId,
    productName: m.productName ?? '',
    baseGemAmount: m.baseGemAmount,
    bonusGemAmount: m.bonusGemAmount,
    promoGemAmount: m.promoGemAmount,
    membershipGemAmount: m.membershipGemAmount,
    iconUrl: m.iconUrl ?? '',
    backgroundColor: (m.content as { backgroundColor?: string })?.backgroundColor ?? '',
    price: m.pgPrice?.price ?? 0,
    currency: m.pgPrice?.currency ?? '',
    discountRate:
      m.discountRate && Number.isFinite(Number(m.discountRate)) && Number(m.discountRate) > 0
        ? Number(m.discountRate)
        : undefined,
    moduleEndDate: m.moduleEndDate,
    purchaseLimitExceeded: m.purchaseLimitExceeded,
    purchaseLimitBehavior: m.purchaseLimitBehavior,
  }));

export const toSubscriptionProducts = (section?: DTOSubProductsSection): SubscriptionProduct[] =>
  (section?.modules ?? []).map((m: DTOSubProductModule) => ({
    moduleId: m.moduleId,
    productName: m.productName ?? '',
    iconUrl: m.iconUrl ?? '',
    productId: m.productId,
    categoryId: m.categoryId,
    content: m.content,
    introductoryDisplayDiscountRate: m.introductoryDisplayDiscountRate,
    badge: m.badge,
    purchaseLimitExceeded: m.purchaseLimitExceeded,
    purchaseLimitBehavior: m.purchaseLimitBehavior,
  }));

export const toStarterPackageGemProduct = (
  starterPackage: StarterPackageModule,
  singleProduct: SingleProductModule
): GemProduct | undefined => {
  if (!starterPackage.pgPrice) return undefined;

  const saveRate = getSaveRateByGemProductComparison(starterPackage, singleProduct);

  return {
    productId: starterPackage.productId,
    productName: starterPackage.productName ?? '',
    baseGemAmount: starterPackage.baseGemAmount,
    bonusGemAmount: starterPackage.bonusGemAmount,
    promoGemAmount: starterPackage.promoGemAmount,
    membershipGemAmount: starterPackage.membershipGemAmount,
    iconUrl: starterPackage.iconUrl,
    backgroundColor: starterPackage.content.iconBackgroundColor,
    price: starterPackage.pgPrice.price,
    currency: starterPackage.pgPrice.currency,
    discountRate: saveRate > 0 ? saveRate : undefined,
    moduleEndDate: starterPackage.moduleEndDate,
    purchaseLimitExceeded: starterPackage.purchaseLimitExceeded,
    purchaseLimitBehavior: starterPackage.purchaseLimitBehavior,
  };
};

export const filterVisibleGemProducts = (products: GemProduct[]): GemProduct[] =>
  products.filter(
    (p) =>
      !(p.purchaseLimitExceeded && p.purchaseLimitBehavior === PURCHASE_LIMIT_BEHAVIOR_TYPES.HIDDEN)
  );
